import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { RefreshTokenService } from '../service/refreshToken.service';
import {
  CreditCountObservable,
  StateService,
} from '../service/observable.service';
import { CognitoService } from '../service/cognito.service';
import { AuthStorageService } from '../service/auth-storage.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private refreshTokenService: RefreshTokenService,
    private creditCountObservable: CreditCountObservable,
    private cognitoService: CognitoService,
    private stateService: StateService,
    private authStorage: AuthStorageService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const accessToken = this.authStorage.getItem('accessToken');
    if (accessToken) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
    }
    return next.handle(request).pipe(
      // tap and retrieve x-talonic-credits header
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const xTalonicCredits = event.headers.get('x-talonic-credits');
          if (xTalonicCredits) {
            this.creditCountObservable.registerNewUpdate(
              parseInt(xTalonicCredits)
            );
          }
        }
      }),
      // tap and log x-correlation-id header
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const correlationId = event.headers.get('x-correlation-id');

          if (!correlationId) {
            return;
          }

          let operationType = '';
          if (request.url.includes('table-extractor/process-file')) {
            operationType = 'structuring';
            this.stateService.setCorrelationId(correlationId);
          }

          if (request.url.includes('tables/upload_file')) {
            operationType = 'upload';
            this.stateService.setCorrelationId(correlationId);
          }

          if (operationType) {
            console.log(
              `Correlation ID for ${operationType} request: ${correlationId}`
            );
          }
        }
      }),
      // tap and retrieve x-talonic-credits header
      catchError((error) => {
        console.log(`Error at request:`, error);
        if (error instanceof HttpErrorResponse) {
          // Proper HTTP error
          if (error.status === 401 && error.error.type == 'TokenExpired') {
            // Current accessToken is expired, try to refresh it
            console.log(`Token expired, attempting refresh..`);
            return this.tryTokenRefresh(request, next);
          }
          if (error.message == 'Access Token has been revoked') {
            // Cognito has revoked the access token, so we need to logout the user
            console.log(`Access token has been revoked, logging out user..`);
            return this.cognitoService
              .signOutAndRedirect()
              .pipe(switchMap(() => throwError(() => error)));
          }
        }
        return throwError(() => error);
      })
    );
  }

  private tryTokenRefresh(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.refreshTokenService.refreshToken().pipe(
      switchMap((token: any) => {
        console.log(`Token refresh successful.`);
        // Repeat the request with updated bearer token
        const clonedRequest = request.clone({
          setHeaders: { Authorization: 'Bearer ' + token },
        });
        return next.handle(clonedRequest);
      }),
      catchError((error) => {
        // Refresh failed, throw the error
        return throwError(() => new Error(error));
      })
    );
  }
}
